import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getCurrentBranch, getSubdomain } from '../lib/branchDetection';

/**
 * Branch Context
 *
 * Provides branch information based on subdomain throughout the app.
 * All components can access the current branch without prop drilling.
 */

const BranchContext = createContext({
  branch: null,
  branchId: null,
  branchName: null,
  subdomain: null,
  loading: true,
  error: null
});

export const useBranchContext = () => {
  const context = useContext(BranchContext);
  if (context === undefined) {
    throw new Error('useBranchContext must be used within a BranchContextProvider');
  }
  return context;
};

export const BranchContextProvider = ({ children }) => {
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
  const subdomain = getSubdomain();

  useEffect(() => {
    const initBranch = async () => {
      if (!organizationId) {
        setError('Organization ID not configured');
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const { branch: branchData, error: branchError } = await getCurrentBranch(organizationId);

        if (branchError) {
          console.error('Error loading branch:', branchError);
          setError(branchError);
        } else {
          setBranch(branchData);
        }
      } catch (err) {
        console.error('Error in branch initialization:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initBranch();
  }, [organizationId]);

  const value = {
    branch,
    branchId: branch?.id || null,
    branchName: branch?.name || null,
    subdomain,
    loading,
    error
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

BranchContextProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default BranchContext;
